import { Component, Input, inject, DoCheck } from '@angular/core';
import { Router } from '@angular/router';
import { AufnahmePunktDocument, Baumart, BodenvegetationZeile, hatHumusForm, hatVegetationsformengruppe, humusformLabel, schichtLabel, AltNeu } from '../../../data-model';
import { ProjectService } from 'src/app/project.service';
import { verfahrenVon } from '../../verfahren/verfahren';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-vegetation',
    templateUrl: './bohrpunkt-vegetation.component.html',
    styleUrls: ['../hl-table.component.css', './bohrpunkt-vegetation.component.css'],

})
export class BohrpunktVegetationComponent implements DoCheck {
	humusformLabel = humusformLabel;
	hatHumusForm = hatHumusForm;
	hatVegetationsformengruppe = hatVegetationsformengruppe;
	schichtLabel = schichtLabel;

	private router = inject(Router);
	private ps = inject(ProjectService);

  @Input() bp!: AufnahmePunktDocument;
  @Input() printView!: boolean;

	// Gecachte Arrays — werden nur neu berechnet, wenn sich die Artenliste geändert hat (nicht bei jedem Render)
	cachedSchichten: ('b1'|'b2'|'str'|'kr1'|'kr2'|'m')[] = [];
	cachedVegetationen: (BodenvegetationZeile & { rowspan: number; groupEven: boolean })[] = [];
	private letzterStand?: string;

	/**
	 * Am Inhalt der Artenliste, nicht am Punkt: „Aus anderem Punkt übernehmen" ersetzt die
	 * Vegetation im SELBEN Punkt. Mit `ngOnChanges` allein stand die alte Liste da, bis man
	 * das Protokoll schloss und wieder öffnete (Nutzer-Befund 02.10.2026). Dieselbe Bauart
	 * wie Boden-Reiter und Grubenbild.
	 */
	ngDoCheck() {
		const stand = this.standDerArten();
		if (stand === this.letzterStand) return;
		this.letzterStand = stand;
		this.updateCaches();
	}

	private standDerArten(): string {
		const zeilen = this.bp?.bodenvegetation?.aufnahme?.vegetationen ?? [];
		return zeilen.map(z => `${z.schicht ?? ''}|${z.vegetation ?? ''}|${z.haeufigkeit ?? ''}`).join('\n');
	}

	private updateCaches() {
		this.cachedSchichten = this.calcSchichten();
		this.cachedVegetationen = this.calcSortedVegetationen();
	}

	/**
	 * Aufnahme nach SEA 95? Nur dann gehoeren Humusform, Vegetationsformengruppe und
	 * die Bestandesbeschreibung in diesen Reiter: Das sind forstliche Bewertungen.
	 *
	 * Die Vegetationsaufnahme selbst (Schichtanteile und Artenliste) wird auch bei
	 * einer Aufnahme nach der Bodenkundlichen Kartieranleitung angeboten
	 * (Nutzer-Entscheidung 20.09.2026) - ihr Bogen fragt in Feld 19 ohnehin nach der
	 * Vegetation. Die Humusform hat die KA in ihrer Profilkennzeichnung (Feld 63);
	 * stuende die SEA-Humusform daneben, gaebe es zwei davon in einem Punkt.
	 */
	get istSeaPunkt(): boolean {
		return verfahrenVon(this.bp) === 'sea95';
	}

	get canEdit(): boolean {
		return this.ps.canIWritePoint(this.bp) && !!this.bp?.header?.projects?.some(p => p.id === this.ps.project());
	}

	openVegetation() {
		// Symmetrisch zum Klick-Gate der HlAreaDirective (hl-area.directive.ts):
		// in die Edit-Form nur navigieren, wenn der Punkt dem aktiven Projekt
		// zugeordnet UND schreibbar ist (`canEdit` = canIWritePoint + Zuordnung).
		// Sonst bleibt Vegetation — wie alle anderen Tabs — read-only.
		if (!this.canEdit) return;
		this.router.navigate(['/bohrpunkte', this.bp._id, 'Vegetationsaufnahme']);
	}
	
	private static readonly SCHICHT_ORDER: readonly ('b1'|'b2'|'str'|'kr1'|'kr2'|'m')[] = ['b1', 'b2', 'str', 'kr1', 'kr2', 'm'];

	private calcSchichten(): ('b1'|'b2'|'str'|'kr1'|'kr2'|'m')[] {
		const schichten = new Set<'b1'|'b2'|'str'|'kr1'|'kr2'|'m'>();
		this.bp.bodenvegetation.aufnahme.vegetationen.forEach((bv: BodenvegetationZeile) => {
			if (bv.schicht) schichten.add(bv.schicht);
		});
		return BohrpunktVegetationComponent.SCHICHT_ORDER.filter(s => schichten.has(s));
	}

	private schichtOrder: Record<string, number> = {
		'b1': 1, 'b2': 2, 'str': 3, 'kr1': 4, 'kr2': 5, 'm': 6
	};

	private calcSortedVegetationen(): (BodenvegetationZeile & { rowspan: number; groupEven: boolean })[] {
		const sorted = [...this.bp.bodenvegetation.aufnahme.vegetationen].sort((a, b) => {
			const schichtA = this.schichtOrder[a.schicht || ''] || 99;
			const schichtB = this.schichtOrder[b.schicht || ''] || 99;
			if (schichtA !== schichtB) return schichtA - schichtB;
			const haeufA = parseFloat(a.haeufigkeit) || 0;
			const haeufB = parseFloat(b.haeufigkeit) || 0;
			return haeufB - haeufA;
		});
		let groupIndex = 0;
		return sorted.map((row, i) => {
			const isFirst = i === 0 || row.schicht !== sorted[i - 1].schicht;
			if (isFirst && i > 0) groupIndex++;
			const rowspan = isFirst ? sorted.filter(r => r.schicht === row.schicht).length : 0;
			return { ...row, rowspan, groupEven: groupIndex % 2 === 0 };
		});
	}

	humusformDisplay(): string {
		if (hatVegetationsformengruppe(this.bp))
			return this.bp.header?.vegetationsformengruppe ?? '';
		const hf = this.bp.header?.humusform?.aufnahmeFlaeche as AltNeu<string> | undefined;
		if (!hf) return '';
		return [hf.neu, hf.alt].filter(v => !!v).join(' / ');
	}

	baumarten(): string {
		if (typeof this.bp.extendedHeader.baumarten === 'string') return this.bp.extendedHeader.baumarten;

		const baumarten = this.bp.extendedHeader.baumarten as Baumart[];
		let bas = '';
		for (const ba of baumarten) bas += ba.art + '';

		return bas;
	}
}
